import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Frown, Meh, Smile, Loader2, ArrowRight, Star, CheckCircle2, Phone } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import Cta from "@/components/Cta";
import { submitLead } from "@/lib/api";
import { fbTrack, fbTrackCustom, gaEvent } from "@/lib/track";
import { SITE } from "@/lib/site";

const GOOGLE_REVIEW_URL = "https://g.page/r/CVgVjhhEybFNEAI/review";

const OPTIONS = [
  { key: "unhappy", label: "Unhappy", Icon: Frown, active: "bg-rose-500/15 border-rose-500 text-rose-400", ring: "hover:border-rose-500/60" },
  { key: "neutral", label: "Okay", Icon: Meh, active: "bg-amber-500/15 border-amber-500 text-amber-400", ring: "hover:border-amber-500/60" },
  { key: "happy", label: "Happy", Icon: Smile, active: "bg-emerald-500/15 border-emerald-500 text-emerald-400", ring: "hover:border-emerald-500/60" },
];

export default function Feedback() {
  const [params] = useSearchParams();
  const name = useMemo(() => (params.get("name") || "").replace(/_/g, " ").trim(), [params]);
  const firstName = useMemo(() => name.split(" ")[0] || "", [name]);

  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!rating) {
      toast.error("Please choose a face so we know how we did.");
      return;
    }
    setLoading(true);
    try {
      const label = OPTIONS.find((o) => o.key === rating)?.label || rating;
      await submitLead({
        type: "feedback",
        name: name || "Anonymous",
        rating: label,
        message: comment,
      });
      fbTrack("Lead", { content_name: "feedback", content_category: label });
      gaEvent("generate_lead", { form_type: "feedback", event_category: "lead", event_label: label });
      setSubmitted(true);
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + SITE.phone + ".");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title="Share Your Feedback | CYAN Wireless" description="Tell us how we're doing. Your feedback helps CYAN Wireless serve rural Colorado better." path="/feedback" />
      <section className="relative min-h-[85vh] grid place-items-center bg-ink-950 overflow-hidden pt-28 pb-20 px-5">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-cyan-500/10 blur-[150px]" />
        <div className="relative z-10 w-full max-w-xl">
          <AnimatePresence mode="wait">
            {!submitted ? (
              <motion.div key="form" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }}>
                <p className="overline mb-4 flex items-center gap-3"><span className="w-8 h-px bg-cyan-500" />Your feedback</p>
                <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-white leading-[0.95]">
                  {firstName ? <>Hi {firstName},<br />we always <span className="text-cyan-400 text-glow">appreciate</span> your feedback</> : <>We always <span className="text-cyan-400 text-glow">appreciate</span> your feedback</>}
                </h1>
                <p className="mt-5 text-zinc-400">…to be sure we're providing the best service possible. Tap a face below and add a comment if you'd like — it only takes a moment.</p>

                <form onSubmit={onSubmit} className="mt-10 space-y-8" data-testid="feedback-form">
                  <div className="grid grid-cols-3 gap-3 sm:gap-4">
                    {OPTIONS.map(({ key, label, Icon, active, ring }) => {
                      const on = rating === key;
                      return (
                        <button
                          type="button"
                          key={key}
                          onClick={() => setRating(key)}
                          data-testid={`feedback-face-${key}`}
                          aria-pressed={on}
                          className={`group flex flex-col items-center gap-3 rounded-2xl border py-6 transition-colors ${on ? active : `border-white/10 bg-ink-900 text-zinc-400 ${ring}`}`}
                        >
                          <Icon size={44} strokeWidth={1.6} className="transition-transform group-hover:scale-110" />
                          <span className="font-mono-tech text-[11px] uppercase tracking-widest">{label}</span>
                        </button>
                      );
                    })}
                  </div>

                  <div>
                    <label className="overline block mb-2">Comments (optional)</label>
                    <textarea
                      rows={4}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Tell us what went well or how we can improve…"
                      data-testid="feedback-comment"
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-zinc-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-colors"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="feedback-submit"
                    className="w-full inline-flex items-center justify-center gap-2 bg-cyan-500 text-ink-950 font-display font-bold uppercase tracking-widest px-8 py-4 rounded-xl btn-glow hover:bg-cyan-400 disabled:opacity-60"
                  >
                    {loading ? <Loader2 className="animate-spin" size={18} /> : <ArrowRight size={18} />}
                    {loading ? "Sending…" : "Submit Feedback"}
                  </button>
                </form>
              </motion.div>
            ) : rating === "happy" ? (
              <motion.div key="happy" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="text-center" data-testid="feedback-thankyou-happy">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 15 }} className="inline-grid place-items-center w-20 h-20 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 mb-8">
                  <Smile size={40} />
                </motion.div>
                <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-white leading-[0.95]">
                  {name ? `Thanks, ${name}!` : "Thank you!"}
                </h1>
                <p className="mt-5 text-lg text-zinc-400 max-w-md mx-auto">
                  We're so glad you're happy with CYAN Wireless. Would you take a moment to leave us a Google review? It helps neighbors find reliable local internet.
                </p>
                <div className="mt-10 flex flex-wrap gap-4 justify-center">
                  <Cta href={GOOGLE_REVIEW_URL} data-testid="feedback-google-review" onClick={() => fbTrackCustom("LeaveReview", { platform: "google" })}>
                    <Star size={16} /> Leave a Google Review
                  </Cta>
                  <Cta to="/" variant="secondary">Back Home</Cta>
                </div>
              </motion.div>
            ) : (
              <motion.div key="thanks" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="text-center" data-testid="feedback-thankyou">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 15 }} className="inline-grid place-items-center w-20 h-20 rounded-full bg-cyan-500/15 border border-cyan-500/30 text-cyan-400 mb-8">
                  <CheckCircle2 size={40} />
                </motion.div>
                <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-white leading-[0.95]">
                  {name ? `Thank you, ${name}.` : "Thank you."}
                </h1>
                <p className="mt-5 text-lg text-zinc-400 max-w-md mx-auto">
                  We appreciate you taking the time. Your feedback goes straight to our team and helps us do better. If something's not right, we'd love to make it right — give us a call or text.
                </p>
                <div className="mt-10 flex flex-wrap gap-4 justify-center">
                  <Cta href={`tel:${SITE.phoneRaw}`}><Phone size={16} /> {SITE.phone}</Cta>
                  <Cta to="/" variant="secondary">Back Home</Cta>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </>
  );
}
